import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlertTriangle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pflichtmodal vor jeder Statuswechsel auf "Freigegeben".
 * Bestätigt 4 Haftungstexte, erfasst Name + Rolle, 2 Pflicht-Checkboxen.
 * Erst nach erfolgreicher API-Antwort onConfirmed(freigabe-Objekt).
 */
export default function PkiFreigabeModal({
  open, onClose, draftId, outputText, editedBefore, defaultName, defaultRole, kindLabel,
  halluzinationscheck, onConfirmed,
}) {
  const [info, setInfo] = useState(null);
  const [name, setName] = useState(defaultName || "");
  const [rolle, setRolle] = useState(defaultRole || "");
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [checkKritisch, setCheckKritisch] = useState(false);
  const [begruendung, setBegruendung] = useState("");
  const [busy, setBusy] = useState(false);
  const [nowStr, setNowStr] = useState("");

  const isRed = halluzinationscheck?.severity === "rot";
  const findingCount = halluzinationscheck?.count || 0;

  useEffect(() => {
    if (!open) return;
    setNowStr(new Date().toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) + " Uhr");
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/freigabe/info`, { withCredentials: true });
        setInfo(r.data);
      } catch (e) {
        toast.error("Konnte Haftungstexte nicht laden.");
      }
    })();
  }, [open]);

  if (!open) return null;

  const baseValid = !!(name.trim() && rolle.trim() && check1 && check2);
  const redValid = !isRed || (checkKritisch && begruendung.trim().length >= 10);
  const canSubmit = baseValid && redValid && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/freigabe`, {
        draft_id: draftId,
        pruefer_name: name.trim(),
        pruefer_rolle: rolle.trim(),
        check_fachlich_geprueft: check1,
        check_verantwortung: check2,
        output_text: outputText,
        edited_before: !!editedBefore,
        check_kritische_hinweise_geprueft: isRed ? checkKritisch : false,
        begruendung_kritisch: isRed ? begruendung.trim() : null,
      }, { withCredentials: true });
      toast.success("Freigabe erteilt · Audit-Trail gespeichert.");
      onConfirmed?.(r.data?.freigabe || null);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="pki-freigabe-modal"
      className="fixed inset-0 z-50 bg-black/55 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white rounded-3xl border border-cream-300 max-w-2xl w-full p-6 sm:p-8 shadow-2xl my-4">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-darkred text-cream-50">
              <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
                Pflegefachliche Freigabe · {kindLabel || "KI-Entwurf"}
              </div>
              <h3 className="font-display text-xl text-sand-800 mt-1 leading-tight">
                Haftungsbestätigung erforderlich
              </h3>
              <div className="text-[12px] text-sand-500 mt-1">
                Datum / Uhrzeit der Freigabe: <strong>{nowStr}</strong>
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="pki-freigabe-close"
            className="text-sand-500 hover:text-sand-800 p-1">
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="rounded-2xl border border-saffron/40 bg-saffron/10 p-4 mb-5">
          <div className="flex items-start gap-2 text-[12.5px] text-clay-dark">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              Mit deiner Freigabe übernimmst du die pflegefachliche Verantwortung für diesen Entwurf.
              Bitte bestätige jeden Punkt nur, wenn er für dich zutrifft.
            </div>
          </div>
        </div>

        {info?.haftungstexte && (
          <div className="space-y-2 mb-5" data-testid="pki-freigabe-texte">
            {info.haftungstexte.map((t, i) => (
              <div key={i} className="flex items-start gap-2 rounded-xl bg-cream-50 border border-cream-300 p-3">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-dark text-cream-50 text-[10px] font-bold mt-0.5">
                  {i + 1}
                </span>
                <p className="text-[13.5px] text-sand-800 leading-snug">{t}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
              Name der prüfenden Pflegefachkraft *
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Anna Mustermann"
              data-testid="pki-freigabe-name"
              className="w-full h-11 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
              Rolle / Funktion *
            </label>
            <input
              value={rolle}
              onChange={(e) => setRolle(e.target.value)}
              placeholder="z. B. PDL, Pflegefachkraft, Praxisanleitung"
              data-testid="pki-freigabe-rolle"
              className="w-full h-11 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
            />
          </div>
        </div>

        <div className="space-y-2 mb-6">
          <label className="flex items-start gap-2 cursor-pointer rounded-xl border border-cream-300 bg-cream-50 p-3 hover:border-terra transition-colors">
            <input type="checkbox" checked={check1}
              onChange={(e) => setCheck1(e.target.checked)}
              data-testid="pki-freigabe-check1"
              className="h-4 w-4 mt-0.5 accent-terra shrink-0" />
            <span className="text-[13px] text-sand-800 leading-snug">
              <strong>Fachlich geprüft:</strong> Ich habe den KI-Entwurf vollständig gelesen und pflegefachlich geprüft.
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer rounded-xl border border-cream-300 bg-cream-50 p-3 hover:border-terra transition-colors">
            <input type="checkbox" checked={check2}
              onChange={(e) => setCheck2(e.target.checked)}
              data-testid="pki-freigabe-check2"
              className="h-4 w-4 mt-0.5 accent-terra shrink-0" />
            <span className="text-[13px] text-sand-800 leading-snug">
              <strong>Verantwortung übernommen:</strong> Ich übernehme die pflegefachliche Verantwortung
              für diese Dokumentation in meiner Rolle.
            </span>
          </label>
        </div>

        {/* Red-Severity Override · Halluzinationscheck */}
        {isRed && (
          <div
            data-testid="pki-freigabe-kritisch-block"
            className="rounded-2xl border-2 border-red-400 bg-red-50 p-4 mb-6"
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-red-700" strokeWidth={2} />
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-red-800">
                  Kritische Hinweise vorhanden
                </div>
                <p className="text-[13px] text-red-900 leading-snug mt-1">
                  Der automatische Plausibilitätscheck hat <strong>{findingCount} kritische Hinweis{findingCount === 1 ? "" : "e"}</strong> gefunden.
                  Eine Freigabe ist nur mit Bestätigung und Begründung möglich.
                </p>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer rounded-xl bg-white border border-red-300 p-3 mb-3 hover:border-red-500 transition-colors">
              <input type="checkbox" checked={checkKritisch}
                onChange={(e) => setCheckKritisch(e.target.checked)}
                data-testid="pki-freigabe-check-kritisch"
                className="h-4 w-4 mt-0.5 accent-red-700 shrink-0" />
              <span className="text-[13px] text-red-900 leading-snug">
                <strong>Hinweise geprüft:</strong> Ich habe die kritischen Hinweise gelesen und pflegefachlich abgewogen.
              </span>
            </label>

            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-red-800 mb-1">
                Begründung der Freigabe trotz Hinweis * (mind. 10 Zeichen)
              </span>
              <textarea
                value={begruendung}
                onChange={(e) => setBegruendung(e.target.value)}
                rows={3}
                placeholder="z. B. „Maßnahme entspricht aktueller fachlicher Einschätzung trotz scheinbarem Widerspruch zur SIS, weil …"
                data-testid="pki-freigabe-begruendung"
                className="w-full rounded-xl border border-red-300 bg-white px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-red-600"
              />
              <span className={`block text-[11px] mt-1 ${begruendung.trim().length >= 10 ? "text-emerald-700" : "text-red-700"}`}>
                {begruendung.trim().length}/10 Zeichen
              </span>
            </label>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 flex-wrap">
          <Button onClick={onClose} variant="outline" size="sm"
            data-testid="pki-freigabe-cancel"
            className="h-11 rounded-full border-clay px-5">
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={!canSubmit}
            data-testid="pki-freigabe-submit"
            className="h-11 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white px-6 font-semibold">
            {busy ? "Speichere Freigabe …" : "Verbindlich freigeben"}
          </Button>
        </div>

        <p className="mt-4 text-[11px] text-sand-500 text-center leading-snug">
          Audit-Trail wird gespeichert (Name · Rolle · Zeitpunkt · Dokumentart · ob vorher bearbeitet wurde).
        </p>
      </div>
    </div>
  );
}
